import {
    DicomStructureSet,
    RoiInterpretedType,
    ROI_POINT_TYPES,
    DicomContour,
    ContourGeometricType,
} from '../../dicom/dicomstructureset';
import { DicomRoi } from '../../dicom/dicomRoi';
import { DcmJsDatasetWrapper } from '../../dicom/dcmJsWrapper';
import {
    DicomProcessor,
    generateUniqueStructureSetLabel,
} from '../dicomProcessor';
import { Image3D } from '../../patient/image3d';
import { StructureSet } from '../../patient/structureset';
import { DICOMApprovalStatus } from '../../dicom/dicomConstants';
import { RigidRegistration } from '../../patient/rigidRegistration';
import { ReadonlyMat4, mat4, vec3 } from 'gl-matrix';
import { SdfResamplingService } from '~/engine/util/algorithms/sdf/SdfResamplingService';
import { Environment } from '~/engine/data/Environment';
import { ApprovedStructureError } from '../../errors/DomainError';
import { generateUID } from '~/utils/dicomUid';
import type { RoiId } from '../../changes/ids';
import { StructureSetService } from '../structureSet/StructureSetService';
import { CopySkipReason, planCopyTargets } from './copyTargetPlan';

/**
 * Interpreted type a copied ROI may carry in the target set, and whether
 * getting there required a DEMOTION: EXTERNAL becomes UNKNOWN when the target
 * already contains an EXTERNAL (body contour) ROI — the domain enforces at
 * most one per set ({@link DuplicateExternalRoiError} from createRoi), and a
 * copy must still succeed rather than fail on the guard.
 *
 * The demotion is REPORTED, never absorbed: a structure that silently stops
 * being the body contour changes what downstream dose calculation and export
 * treat as the patient outline, so every caller must be able to tell the user.
 * The domain only reports it — see
 * {@link CopyStructureSetResult.externalDemotions} for the set-level copies
 * and this return value for the single-ROI copy, which shares the rule from
 * here rather than re-deriving it.
 */
export function copyableInterpretedType(
    target: DicomStructureSet,
    type: RoiInterpretedType | undefined
): {
    readonly interpretedType: RoiInterpretedType | undefined;
    readonly demotedFromExternal: boolean;
} {
    if (type === RoiInterpretedType.EXTERNAL && target.findExternalRoi()) {
        return {
            interpretedType: RoiInterpretedType.UNKNOWN,
            demotedFromExternal: true,
        };
    }
    return { interpretedType: type, demotedFromExternal: false };
}

export interface CopyStructureSetParams {
    readonly sourceStructureSet: StructureSet;
    readonly targetImage: Image3D;
    readonly existingStructureSets: readonly StructureSet[];
    readonly selectedRoiIds?: ReadonlySet<RoiId>;
    readonly label?: string;
}

export interface CopyWithRegistrationParams {
    readonly sourceStructureSet: StructureSet;
    readonly targetImage: Image3D;
    readonly registration: RigidRegistration;
    readonly existingStructureSets: readonly StructureSet[];
    readonly selectedRoiIds?: ReadonlySet<RoiId>;
    readonly label?: string;
}

export interface CopyIntoExistingParams {
    readonly sourceStructureSet: StructureSet;
    readonly targetStructureSet: StructureSet;
    readonly selectedRoiIds: ReadonlySet<RoiId>;
    readonly overwriteExisting?: boolean;
}

export interface CopyWithRegistrationIntoExistingParams {
    readonly sourceStructureSet: StructureSet;
    readonly targetStructureSet: StructureSet;
    readonly targetImage: Image3D;
    readonly registration: RigidRegistration;
    readonly selectedRoiIds: ReadonlySet<RoiId>;
    readonly overwriteExisting?: boolean;
    readonly label?: string;
}

export interface CopySameFrameOfReferenceParams {
    readonly sourceStructureSet: StructureSet;
    readonly targetImage: Image3D;
    readonly existingStructureSets: readonly StructureSet[];
    readonly selectedRoiIds?: ReadonlySet<RoiId>;
    readonly label?: string;
}

export interface CopySameFrameOfReferenceIntoExistingParams {
    readonly sourceStructureSet: StructureSet;
    readonly targetStructureSet: StructureSet;
    readonly targetImage: Image3D;
    readonly selectedRoiIds: ReadonlySet<RoiId>;
    readonly overwriteExisting?: boolean;
}

export interface CopyStructureSetResult {
    structureSet: StructureSet;
    copiedRoiIds: RoiId[];
    skippedRois: SkippedRoi[];
    /**
     * Copied non-point ROIs that ended up with no volume (every contour has
     * fewer than 3 vertices). Point ROIs are excluded — they legitimately
     * carry a single coordinate. Computed in the domain so callers never need
     * to inspect contour geometry themselves.
     */
    emptyVolumeRoiIds: RoiId[];
    /**
     * Copied ROIs that are present in the target but under a generated
     * temporary name, because the final rename back to the source name
     * failed. The contour data is intact; only the label is wrong, so this
     * is reported separately from {@link skippedRois} (nothing copied) and
     * must be surfaced to the user rather than presented as a clean copy.
     */
    namingFailures: RoiNamingFailure[];
    /**
     * Copied structures that could not keep their EXTERNAL interpreted type
     * because the target already has one, and were created as UNKNOWN
     * instead ({@link copyableInterpretedType}). The contour data is intact —
     * only the type differs from the source — but the copy is no longer the
     * target's body contour, so this must be surfaced rather than absorbed.
     */
    externalDemotions: ExternalTypeDemotion[];
}

/**
 * A copied structure whose EXTERNAL type was demoted to UNKNOWN in the target.
 */
export interface ExternalTypeDemotion {
    /** The copied structure's id in the target set. */
    readonly roiId: RoiId;
    readonly roiName: string;
}

/**
 * A copied ROI left under a temporary name by a failed finalization.
 */
export interface RoiNamingFailure {
    readonly roiId: RoiId;
    readonly intendedName: string;
    readonly actualName: string;
}

/**
 * Domain-layer description of a copy destination. Mirrors the shape the UI
 * collects, but only with domain objects — UI view-models map onto this at the
 * application boundary.
 *
 * The variant is chosen by field presence, exactly as {@link copyToTarget}
 * dispatches:
 *  - `targetStructureSet` set    → copy into that existing structure set
 *  - `registration` set          → copy across frames of reference
 *  - `sameFrameOfReference` true → copy within the same frame of reference
 *  - otherwise                   → copy onto a geometry-matched image
 */
export interface CopyToTargetParams {
    readonly sourceStructureSet: StructureSet;
    readonly targetImage: Image3D;
    readonly selectedRoiIds: ReadonlySet<RoiId>;
    readonly existingStructureSets: readonly StructureSet[];
    readonly targetStructureSet?: StructureSet;
    readonly registration?: RigidRegistration;
    readonly sameFrameOfReference?: boolean;
    readonly overwriteExisting?: boolean;
}

export interface SkippedRoi {
    /** The source structure's id. */
    readonly roiId: RoiId;
    readonly roiName: string;
    readonly reason: string;
}

export interface StructureReplacementResult {
    readonly success: boolean;
    /** The replacement's id, which is the replaced structure's. */
    readonly roiId?: RoiId;
    /**
     * Set when the replacement committed but could not be renamed back to
     * the source name: the structure is in the target under this temporary
     * name. `success` stays true because the contour data did land.
     */
    readonly temporaryName?: string;
    /**
     * Set when the replacement structure had to be created as UNKNOWN because
     * the source was EXTERNAL and the target already has one. Propagated so
     * the copy result can report the demotion
     * ({@link CopyStructureSetResult.externalDemotions}).
     */
    readonly demotedFromExternal?: boolean;
    readonly error?: {
        readonly phase: 'validation' | 'creation' | 'deletion' | 'finalization';
        readonly message: string;
        readonly originalError: string;
    };
}

/**
 * Result returned by a {@link ContourResolutionStrategy}.
 *
 * - `contours`  – the resolved contours (already in the target FOR).
 * - `'skip'`    – the ROI should be skipped (no data available).
 */
export type ContourResolutionResult = { contours: DicomContour[] } | 'skip';

/**
 * Strategy that resolves contours for a single ROI during a
 * registration-based copy.
 *
 * Implementations decide *how* to produce target-space contours — e.g. by
 * resampling an SDF texture, transforming point coordinates, or falling back
 * to raw contour vertex transformation.
 *
 * The service calls this once per ROI that needs contour data.
 */
export interface ContourResolutionStrategy {
    resolveContours(
        roiId: RoiId,
        sourceRoi: DicomRoi,
        targetImage: Image3D,
        transformMatrix: ReadonlyMat4
    ): Promise<ContourResolutionResult>;
}

/**
 * Resolves contours by resampling the engine's SDF texture for each ROI.
 * Falls back to point transformation for point ROIs, and reports 'skip'
 * when no SDF data is available.
 */
export class SdfContourResolutionStrategy implements ContourResolutionStrategy {
    constructor(private readonly sourceEnvironment: Environment) {}

    async resolveContours(
        roiId: RoiId,
        _sourceRoi: DicomRoi,
        targetImage: Image3D,
        transformMatrix: ReadonlyMat4
    ): Promise<ContourResolutionResult> {
        const engineRoi = this.sourceEnvironment?.structureSet?.getRoi(roiId);
        const sourceTexture = engineRoi?.texture;

        if (!sourceTexture || sourceTexture.isEmpty) {
            if (engineRoi?.isPointRoi && engineRoi.pointCoordinatesMm) {
                const transformed = vec3.transformMat4(
                    vec3.create(),
                    engineRoi.pointCoordinatesMm as vec3,
                    transformMatrix
                );
                return {
                    contours: [
                        new DicomContour(
                            ContourGeometricType.POINT,
                            [transformed],
                            targetImage.sopClassUid,
                            targetImage.imageSlices[0]?.sopInstanceUid ?? ''
                        ),
                    ],
                };
            }
            return 'skip';
        }

        const newContours =
            await SdfResamplingService.resampleAndExtractContours(
                sourceTexture,
                targetImage,
                transformMatrix
            );

        return { contours: newContours };
    }
}

/** The selected source ROIs that exist, in selection order. */
function selectedSourceRois(
    sourceStructureSet: StructureSet,
    selectedRoiIds: Iterable<RoiId>
): { roiId: RoiId; name: string }[] {
    return [...selectedRoiIds].flatMap((roiId) => {
        const roi = sourceStructureSet.getDicomRoi(roiId);
        return roi ? [{ roiId, name: roi.name }] : [];
    });
}

/**
 * Deletes from `cloned` every ROI whose source structure is not selected.
 * `sourceRoiOf` names each cloned ROI's source structure.
 */
function keepOnlySelectedRois(
    cloned: DicomStructureSet,
    sourceRoiOf: ReadonlyMap<RoiId, DicomRoi>,
    selectedRoiIds: ReadonlySet<RoiId>
): void {
    const unselected = [...cloned.rois.values()].filter((roi) => {
        const source = sourceRoiOf.get(roi.uid);
        return source === undefined || !selectedRoiIds.has(source.uid);
    });
    for (const roi of unselected) {
        cloned.deleteRoi(roi.uid);
    }
}

/** Why a source structure named `name` was left out of the copy. */
function copySkipReasonText(reason: CopySkipReason, name: string): string {
    switch (reason.kind) {
        case 'name-taken':
            return `A structure named "${name}" already exists in the target`;
        case 'ambiguous':
            return `${reason.matchCount} structures named "${name}" exist in the target, so it is not clear which one to overwrite`;
        case 'approved':
            return `The existing structure "${reason.targetName}" is approved and cannot be overwritten`;
        case 'locked':
            return `The existing structure "${reason.targetName}" is locked and cannot be overwritten`;
        case 'repeated-in-selection':
            return `Another selected structure is also named "${name}", and only the first one is copied`;
    }
}

/**
 * Domain service for copying structure sets.
 *
 * Pure domain logic — no UI, persistence, rendering, or framework dependencies.
 */
export class StructureSetCopyService {
    /** Source of the deterministic suffix in {@link nextTemporaryName}. */
    private temporaryNameCounter = 0;

    /**
     * Dispatches to the correct copy variant for the given destination and
     * returns a unified result. The choice is made purely from the domain
     * params (see {@link CopyToTargetParams}); no UI knowledge is involved.
     *
     * Registration / same-FOR / into-existing copies need a
     * {@link ContourResolutionStrategy} to produce target-space contours;
     * geometry-matched same-image copies do not use it.
     */
    public async copyToTarget(
        params: CopyToTargetParams,
        contourStrategy: ContourResolutionStrategy
    ): Promise<CopyStructureSetResult> {
        const {
            sourceStructureSet,
            targetImage,
            selectedRoiIds,
            existingStructureSets,
            targetStructureSet,
            registration,
            sameFrameOfReference,
            overwriteExisting = false,
        } = params;

        if (targetStructureSet) {
            if (registration) {
                return this.copyWithRegistrationIntoExisting(
                    {
                        sourceStructureSet,
                        targetStructureSet,
                        targetImage,
                        registration,
                        selectedRoiIds,
                        overwriteExisting,
                    },
                    contourStrategy
                );
            }
            if (sameFrameOfReference) {
                return this.copySameFrameOfReferenceIntoExisting(
                    {
                        sourceStructureSet,
                        targetStructureSet,
                        targetImage,
                        selectedRoiIds,
                        overwriteExisting,
                    },
                    contourStrategy
                );
            }
            return this.copyIntoExistingStructureSet({
                sourceStructureSet,
                targetStructureSet,
                selectedRoiIds,
                overwriteExisting,
            });
        }

        if (registration) {
            return this.copyWithRegistration(
                {
                    sourceStructureSet,
                    targetImage,
                    registration,
                    existingStructureSets,
                    selectedRoiIds,
                },
                contourStrategy
            );
        }
        if (sameFrameOfReference) {
            return this.copySameFrameOfReference(
                {
                    sourceStructureSet,
                    targetImage,
                    existingStructureSets,
                    selectedRoiIds,
                },
                contourStrategy
            );
        }
        return this.copySameGeometry({
            sourceStructureSet,
            targetImage,
            existingStructureSets,
            selectedRoiIds,
        });
    }

    /**
     * Counts copied non-point ROIs that enclose no area — every contour is
     * geometrically degenerate. Uses the contour's own validity rule
     * ({@link DicomContour.isValid}: at least three vertices that are not all
     * collinear) rather than a raw vertex count, so a slice of collinear
     * points is not mistaken for a real volume. Point ROIs are excluded since
     * a single coordinate is their expected, valid geometry.
     *
     * Pure domain rule: keeps contour-geometry inspection out of UI code.
     */
    public static findEmptyVolumeRois(
        structureSet: StructureSet,
        copiedRoiIds: readonly RoiId[]
    ): RoiId[] {
        const empty: RoiId[] = [];
        for (const roiId of copiedRoiIds) {
            const roi = structureSet.getDicomRoi(roiId);
            if (!roi || roi.isPointRoi) {
                continue;
            }
            const hasVolume = roi.contours.some((contour) => contour.isValid);
            if (!hasVolume) {
                empty.push(roiId);
            }
        }
        return empty;
    }

    public copySameGeometry(
        params: CopyStructureSetParams
    ): CopyStructureSetResult {
        const {
            sourceStructureSet,
            targetImage,
            existingStructureSets,
            selectedRoiIds,
            label,
        } = params;

        const sourceImage = sourceStructureSet.image;
        if (!sourceImage) {
            throw new Error(
                'Cannot copy: source structure set has no associated image.'
            );
        }
        if (!targetImage.frameOfReferenceUid) {
            throw new Error(
                'Cannot copy: target image has no Frame of Reference UID.'
            );
        }

        const sourceAndTargetHaveEqualGeometry =
            sourceImage.grid.hasEqualGeometryTo(targetImage.grid);

        if (!sourceAndTargetHaveEqualGeometry) {
            throw new Error(
                'Cannot copy: source and target images have different geometry.'
            );
        }

        const { clonedDicom, sourceRoiOf } = this.cloneAndReidentify(
            sourceStructureSet.dicomStructureSet,
            targetImage,
            existingStructureSets,
            label
        );

        const copied = new StructureSet(
            clonedDicom,
            targetImage.frameOfReferenceUid,
            targetImage
        );

        copied.setApprovalStatus(DICOMApprovalStatus.UNAPPROVED, '', true);

        if (selectedRoiIds) {
            keepOnlySelectedRois(clonedDicom, sourceRoiOf, selectedRoiIds);
        }

        const copiedRoiIds: RoiId[] = [];
        const skippedRois: SkippedRoi[] = [];

        this.remapAllContourReferences(
            copied,
            sourceRoiOf,
            sourceImage,
            targetImage,
            copiedRoiIds,
            skippedRois
        );

        // The DicomRois above were cloned/deleted at the dicom level, so
        // the domain Roi[] wrapper collection must be re-synced or the
        // copied ROIs are invisible to roi-level handlers (selection etc).
        copied.syncRoiObjects();

        return {
            structureSet: copied,
            copiedRoiIds,
            skippedRois,
            emptyVolumeRoiIds: StructureSetCopyService.findEmptyVolumeRois(
                copied,
                copiedRoiIds
            ),
            // A fresh structure set has no name conflicts to replace, and a
            // whole-set clone carries the source's interpreted types verbatim
            // — nothing passes through the single-EXTERNAL guard.
            namingFailures: [],
            externalDemotions: [],
        };
    }

    public copyIntoExistingStructureSet(
        params: CopyIntoExistingParams
    ): CopyStructureSetResult {
        const {
            sourceStructureSet,
            targetStructureSet,
            selectedRoiIds,
            overwriteExisting = false,
        } = params;

        if (targetStructureSet.dicomStructureSet.isApproved) {
            throw new ApprovedStructureError(
                'Cannot copy into an approved structure set.'
            );
        }

        const copiedRoiIds: RoiId[] = [];
        const skippedRois: SkippedRoi[] = [];
        const namingFailures: RoiNamingFailure[] = [];
        const externalDemotions: ExternalTypeDemotion[] = [];
        // Overwriting deletes the target's contours and bypasses
        // StructureSetService, so the plan carries the approval and lock
        // guard (SR-WOR-H-42-1).
        const plans = planCopyTargets(
            selectedSourceRois(sourceStructureSet, selectedRoiIds),
            targetStructureSet.dicomStructureSet.rois.values(),
            overwriteExisting
        );

        for (const roiId of selectedRoiIds) {
            const sourceRoi = sourceStructureSet.getDicomRoi(roiId);
            if (!sourceRoi) {
                skippedRois.push({
                    roiId,
                    roiName: 'Unknown structure',
                    reason: 'ROI not found in source structure set',
                });
                continue;
            }

            const plan = plans.get(roiId);
            if (!plan) {
                throw new Error('No copy plan for a selected structure');
            }
            if (plan.kind === 'skip') {
                skippedRois.push({
                    roiId,
                    roiName: sourceRoi.name,
                    reason: copySkipReasonText(plan.reason, sourceRoi.name),
                });
                continue;
            }

            if (plan.kind === 'overwrite') {
                const replacementResult = this.replaceStructureTransactionally(
                    targetStructureSet,
                    plan.targetRoiId,
                    sourceRoi,
                    [...sourceRoi.contours]
                );

                if (!replacementResult.success) {
                    skippedRois.push({
                        roiId,
                        roiName: sourceRoi.name,
                        reason: `Failed to replace existing "${sourceRoi.name}": ${
                            replacementResult.error?.message ?? 'unknown error'
                        }`,
                    });
                    continue;
                }

                if (replacementResult.roiId === undefined) {
                    skippedRois.push({
                        roiId,
                        roiName: sourceRoi.name,
                        reason: `Replacement succeeded but no structure returned for "${sourceRoi.name}"`,
                    });
                    continue;
                }

                if (replacementResult.temporaryName !== undefined) {
                    namingFailures.push({
                        roiId: replacementResult.roiId,
                        intendedName: sourceRoi.name,
                        actualName: replacementResult.temporaryName,
                    });
                }

                if (replacementResult.demotedFromExternal) {
                    externalDemotions.push({
                        roiId: replacementResult.roiId,
                        roiName: sourceRoi.name,
                    });
                }

                copiedRoiIds.push(replacementResult.roiId);
                continue;
            }

            let newRoi: DicomRoi | undefined;
            try {
                const { interpretedType, demotedFromExternal } =
                    copyableInterpretedType(
                        targetStructureSet.dicomStructureSet,
                        sourceRoi.interpretedType as RoiInterpretedType
                    );
                newRoi = targetStructureSet.dicomStructureSet.createRoi(
                    interpretedType,
                    sourceRoi.name
                );

                newRoi.color = {
                    R: sourceRoi.color.R,
                    G: sourceRoi.color.G,
                    B: sourceRoi.color.B,
                };

                newRoi.contours = [...sourceRoi.contours];

                targetStructureSet.dicomStructureSet.updateContourSequence(
                    newRoi.uid
                );

                copiedRoiIds.push(newRoi.uid);
                if (demotedFromExternal) {
                    externalDemotions.push({
                        roiId: newRoi.uid,
                        roiName: newRoi.name,
                    });
                }
            } catch (err) {
                // Roll back a half-built ROI so a copy reported as skipped
                // never leaves a partial structure behind in the target.
                if (newRoi !== undefined) {
                    try {
                        targetStructureSet.dicomStructureSet.deleteRoi(
                            newRoi.uid
                        );
                    } catch (cleanupErr) {
                        console.warn(
                            'Failed to clean up a partially copied structure:',
                            cleanupErr
                        );
                    }
                }

                skippedRois.push({
                    roiId,
                    roiName: sourceRoi.name,
                    reason:
                        err instanceof Error
                            ? err.message
                            : 'Unknown error while copying ROI',
                });
            }
        }

        // ROIs were created/replaced on the target at the dicom level;
        // re-sync its domain Roi[] wrappers so the new ROIs are
        // addressable by roi-level handlers (selection etc).
        targetStructureSet.syncRoiObjects();

        return {
            structureSet: targetStructureSet,
            copiedRoiIds,
            skippedRois,
            emptyVolumeRoiIds: StructureSetCopyService.findEmptyVolumeRois(
                targetStructureSet,
                copiedRoiIds
            ),
            namingFailures,
            externalDemotions,
        };
    }

    /**
     * Copies a structure set onto a target image linked via a rigid
     * registration. A {@link ContourResolutionStrategy} controls
     * how contour data is produced for each ROI (e.g. SDF resampling).
     */
    public async copyWithRegistration(
        params: CopyWithRegistrationParams,
        contourStrategy: ContourResolutionStrategy
    ): Promise<CopyStructureSetResult> {
        const { sourceStructureSet, targetImage, registration } = params;

        const sourceImage = sourceStructureSet.image;
        if (!sourceImage) {
            throw new Error(
                'Cannot copy: source structure set has no associated image.'
            );
        }
        if (!targetImage.frameOfReferenceUid) {
            throw new Error(
                'Cannot copy: target image has no Frame of Reference UID.'
            );
        }

        const transformMatrix = registration.getTransformFromTo(
            sourceImage.frameOfReferenceUid,
            targetImage.frameOfReferenceUid
        );
        if (!transformMatrix) {
            throw new Error(
                'Registration does not provide a transformation between the source and target images.'
            );
        }

        return this.copyWithTransform(
            sourceStructureSet,
            targetImage,
            transformMatrix,
            contourStrategy,
            params.existingStructureSets,
            params.selectedRoiIds,
            params.label
        );
    }

    /**
     * Copies a structure set to a target image that shares the same Frame
     * of Reference but has different geometry. Uses an identity transform
     * since same-FOR images are already in the same patient coordinate space.
     */
    public async copySameFrameOfReference(
        params: CopySameFrameOfReferenceParams,
        contourStrategy: ContourResolutionStrategy
    ): Promise<CopyStructureSetResult> {
        const { sourceStructureSet, targetImage } = params;

        const sourceImage = sourceStructureSet.image;
        if (!sourceImage) {
            throw new Error(
                'Cannot copy: source structure set has no associated image.'
            );
        }
        if (!targetImage.frameOfReferenceUid) {
            throw new Error(
                'Cannot copy: target image has no Frame of Reference UID.'
            );
        }

        if (
            sourceImage.frameOfReferenceUid !== targetImage.frameOfReferenceUid
        ) {
            throw new Error(
                'Cannot use same-FOR copy: source and target images have different ' +
                    `Frames of Reference (${sourceImage.frameOfReferenceUid} vs ` +
                    `${targetImage.frameOfReferenceUid}). ` +
                    'Use copyWithRegistration instead.'
            );
        }

        return this.copyWithTransform(
            sourceStructureSet,
            targetImage,
            mat4.create(),
            contourStrategy,
            params.existingStructureSets,
            params.selectedRoiIds,
            params.label
        );
    }

    /**
     * Copies selected ROIs from a source structure set into an existing
     * target structure set, using a rigid registration to transform contours.
     *
     * The {@link ContourResolutionStrategy} controls how contour data is
     * produced (SDF resampling, etc.).
     */
    public async copyWithRegistrationIntoExisting(
        params: CopyWithRegistrationIntoExistingParams,
        contourStrategy: ContourResolutionStrategy
    ): Promise<CopyStructureSetResult> {
        const {
            sourceStructureSet,
            targetStructureSet,
            targetImage,
            registration,
            selectedRoiIds,
            overwriteExisting = false,
        } = params;

        const sourceImage = sourceStructureSet.image;
        if (!sourceImage) {
            throw new Error(
                'Cannot copy: source structure set has no associated image.'
            );
        }

        const transformMatrix = registration.getTransformFromTo(
            sourceImage.frameOfReferenceUid,
            targetImage.frameOfReferenceUid
        );
        if (!transformMatrix) {
            throw new Error(
                'Registration does not provide a transformation between the source and target images.'
            );
        }

        return this.copyWithTransformIntoExisting(
            sourceStructureSet,
            targetStructureSet,
            targetImage,
            transformMatrix,
            contourStrategy,
            selectedRoiIds,
            overwriteExisting
        );
    }

    /**
     * Copies selected ROIs into an existing target structure set on an
     * image that shares the same Frame of Reference but has different
     * geometry. Uses an identity transform.
     */
    public async copySameFrameOfReferenceIntoExisting(
        params: CopySameFrameOfReferenceIntoExistingParams,
        contourStrategy: ContourResolutionStrategy
    ): Promise<CopyStructureSetResult> {
        const {
            sourceStructureSet,
            targetStructureSet,
            targetImage,
            selectedRoiIds,
            overwriteExisting = false,
        } = params;

        const sourceImage = sourceStructureSet.image;
        if (!sourceImage) {
            throw new Error(
                'Cannot copy: source structure set has no associated image.'
            );
        }

        if (!targetImage.frameOfReferenceUid) {
            throw new Error(
                'Cannot copy: target image has no Frame of Reference UID.'
            );
        }

        if (
            sourceImage.frameOfReferenceUid !== targetImage.frameOfReferenceUid
        ) {
            throw new Error(
                'Cannot use same-FOR copy: source and target images have different ' +
                    `Frames of Reference (${sourceImage.frameOfReferenceUid} vs ` +
                    `${targetImage.frameOfReferenceUid}). ` +
                    'Use copyWithRegistrationIntoExisting instead.'
            );
        }

        return this.copyWithTransformIntoExisting(
            sourceStructureSet,
            targetStructureSet,
            targetImage,
            mat4.create(),
            contourStrategy,
            selectedRoiIds,
            overwriteExisting
        );
    }

    /**
     * Internal: copies a structure set to a new target image using the
     * given transform matrix and contour resolution strategy.
     */
    private async copyWithTransform(
        sourceStructureSet: StructureSet,
        targetImage: Image3D,
        transformMatrix: ReadonlyMat4,
        contourStrategy: ContourResolutionStrategy,
        existingStructureSets: readonly StructureSet[],
        selectedRoiIds?: ReadonlySet<RoiId>,
        label?: string
    ): Promise<CopyStructureSetResult> {
        const { clonedDicom, sourceRoiOf } = this.cloneAndReidentify(
            sourceStructureSet.dicomStructureSet,
            targetImage,
            existingStructureSets,
            label
        );

        const copied = new StructureSet(
            clonedDicom,
            targetImage.frameOfReferenceUid,
            targetImage
        );

        copied.setApprovalStatus(DICOMApprovalStatus.UNAPPROVED, '', true);

        if (selectedRoiIds) {
            keepOnlySelectedRois(clonedDicom, sourceRoiOf, selectedRoiIds);
        }

        const copiedRoiIds: RoiId[] = [];
        const skippedRois: SkippedRoi[] = [];
        const roiIdsToDelete: RoiId[] = [];

        for (const roi of copied) {
            const sourceRoi = sourceRoiOf.get(roi.uid);
            if (!sourceRoi) {
                continue;
            }

            try {
                const result = await contourStrategy.resolveContours(
                    sourceRoi.uid,
                    sourceRoi,
                    targetImage,
                    transformMatrix
                );

                if (result === 'skip') {
                    skippedRois.push({
                        roiId: sourceRoi.uid,
                        roiName: roi.name,
                        reason: 'Contour resolution strategy skipped this ROI',
                    });
                    roiIdsToDelete.push(roi.uid);
                    continue;
                }

                roi.contours = result.contours;
                copied.dicomStructureSet.updateContourSequence(roi.uid);
                copiedRoiIds.push(roi.uid);
            } catch (err) {
                skippedRois.push({
                    roiId: sourceRoi.uid,
                    roiName: roi.name,
                    reason:
                        err instanceof Error
                            ? err.message
                            : 'Unknown error during contour resolution',
                });
                roiIdsToDelete.push(roi.uid);
            }
        }

        for (const roiId of roiIdsToDelete) {
            copied.dicomStructureSet.deleteRoi(roiId);
        }

        copied.roiArray.forEach((r: DicomRoi) => {
            r.approvalStatus = DICOMApprovalStatus.UNAPPROVED;
        });

        // See copySameGeometry: dicom-level mutations require a wrapper
        // re-sync before the copy is handed to the domain/UI layers.
        copied.syncRoiObjects();

        return {
            structureSet: copied,
            copiedRoiIds,
            skippedRois,
            emptyVolumeRoiIds: StructureSetCopyService.findEmptyVolumeRois(
                copied,
                copiedRoiIds
            ),
            // A fresh structure set has no name conflicts to replace, and a
            // whole-set clone carries the source's interpreted types verbatim
            // — nothing passes through the single-EXTERNAL guard.
            namingFailures: [],
            externalDemotions: [],
        };
    }

    /**
     * Internal: copies selected ROIs into an existing target structure set
     * using the given transform matrix and contour resolution strategy.
     */
    private async copyWithTransformIntoExisting(
        sourceStructureSet: StructureSet,
        targetStructureSet: StructureSet,
        targetImage: Image3D,
        transformMatrix: ReadonlyMat4,
        contourStrategy: ContourResolutionStrategy,
        selectedRoiIds: ReadonlySet<RoiId>,
        overwriteExisting: boolean
    ): Promise<CopyStructureSetResult> {
        if (targetStructureSet.dicomStructureSet.isApproved) {
            throw new ApprovedStructureError(
                'Cannot copy into an approved structure set.'
            );
        }

        const copiedRoiIds: RoiId[] = [];
        const skippedRois: SkippedRoi[] = [];
        const namingFailures: RoiNamingFailure[] = [];
        const externalDemotions: ExternalTypeDemotion[] = [];
        // Overwriting deletes the target's contours and bypasses
        // StructureSetService, so the plan carries the approval and lock
        // guard (SR-WOR-H-42-1).
        const plans = planCopyTargets(
            selectedSourceRois(sourceStructureSet, selectedRoiIds),
            targetStructureSet.dicomStructureSet.rois.values(),
            overwriteExisting
        );

        for (const roiId of selectedRoiIds) {
            const sourceRoi = sourceStructureSet.getDicomRoi(roiId);
            if (!sourceRoi) {
                skippedRois.push({
                    roiId,
                    roiName: 'Unknown structure',
                    reason: 'ROI not found in source structure set',
                });
                continue;
            }

            const plan = plans.get(roiId);
            if (!plan) {
                throw new Error('No copy plan for a selected structure');
            }
            if (plan.kind === 'skip') {
                skippedRois.push({
                    roiId,
                    roiName: sourceRoi.name,
                    reason: copySkipReasonText(plan.reason, sourceRoi.name),
                });
                continue;
            }

            if (plan.kind === 'overwrite') {
                let resolvedContours: DicomContour[];
                try {
                    const result = await contourStrategy.resolveContours(
                        roiId,
                        sourceRoi,
                        targetImage,
                        transformMatrix
                    );

                    if (result === 'skip') {
                        skippedRois.push({
                            roiId,
                            roiName: sourceRoi.name,
                            reason: 'Contour resolution strategy skipped this ROI',
                        });
                        continue;
                    }

                    resolvedContours = result.contours;
                } catch (err) {
                    skippedRois.push({
                        roiId,
                        roiName: sourceRoi.name,
                        reason:
                            err instanceof Error
                                ? err.message
                                : 'Contour resolution failed',
                    });
                    continue;
                }

                const replacementResult = this.replaceStructureTransactionally(
                    targetStructureSet,
                    plan.targetRoiId,
                    sourceRoi,
                    resolvedContours
                );

                if (!replacementResult.success) {
                    skippedRois.push({
                        roiId,
                        roiName: sourceRoi.name,
                        reason: `Failed to replace existing "${sourceRoi.name}": ${
                            replacementResult.error?.message ?? 'unknown error'
                        }`,
                    });
                    continue;
                }

                if (replacementResult.roiId === undefined) {
                    skippedRois.push({
                        roiId,
                        roiName: sourceRoi.name,
                        reason: `Replacement succeeded but no structure returned for "${sourceRoi.name}"`,
                    });
                    continue;
                }

                if (replacementResult.temporaryName !== undefined) {
                    namingFailures.push({
                        roiId: replacementResult.roiId,
                        intendedName: sourceRoi.name,
                        actualName: replacementResult.temporaryName,
                    });
                }

                if (replacementResult.demotedFromExternal) {
                    externalDemotions.push({
                        roiId: replacementResult.roiId,
                        roiName: sourceRoi.name,
                    });
                }

                copiedRoiIds.push(replacementResult.roiId);
                continue;
            }

            let resolvedContours: DicomContour[];
            try {
                const result = await contourStrategy.resolveContours(
                    roiId,
                    sourceRoi,
                    targetImage,
                    transformMatrix
                );

                if (result === 'skip') {
                    skippedRois.push({
                        roiId,
                        roiName: sourceRoi.name,
                        reason: 'Contour resolution strategy skipped this ROI',
                    });
                    continue;
                }

                resolvedContours = result.contours;
            } catch (err) {
                skippedRois.push({
                    roiId,
                    roiName: sourceRoi.name,
                    reason:
                        err instanceof Error
                            ? err.message
                            : 'Contour resolution failed',
                });
                continue;
            }

            let newRoi: DicomRoi | undefined;
            try {
                const { interpretedType, demotedFromExternal } =
                    copyableInterpretedType(
                        targetStructureSet.dicomStructureSet,
                        sourceRoi.interpretedType as RoiInterpretedType
                    );
                newRoi = targetStructureSet.dicomStructureSet.createRoi(
                    interpretedType,
                    sourceRoi.name
                );

                newRoi.color = {
                    R: sourceRoi.color.R,
                    G: sourceRoi.color.G,
                    B: sourceRoi.color.B,
                };

                newRoi.contours = resolvedContours;
                targetStructureSet.dicomStructureSet.updateContourSequence(
                    newRoi.uid
                );

                copiedRoiIds.push(newRoi.uid);
                if (demotedFromExternal) {
                    externalDemotions.push({
                        roiId: newRoi.uid,
                        roiName: newRoi.name,
                    });
                }
            } catch (err) {
                // Roll back a half-built ROI so a copy reported as skipped
                // never leaves a partial structure behind in the target.
                if (newRoi !== undefined) {
                    try {
                        targetStructureSet.dicomStructureSet.deleteRoi(
                            newRoi.uid
                        );
                    } catch (cleanupErr) {
                        console.warn(
                            'Failed to clean up a partially copied structure:',
                            cleanupErr
                        );
                    }
                }

                skippedRois.push({
                    roiId,
                    roiName: sourceRoi.name,
                    reason:
                        err instanceof Error
                            ? err.message
                            : 'Unknown error while copying ROI',
                });
            }
        }

        // ROIs were created/replaced on the target at the dicom level;
        // re-sync its domain Roi[] wrappers so the new ROIs are
        // addressable by roi-level handlers (selection etc).
        targetStructureSet.syncRoiObjects();

        return {
            structureSet: targetStructureSet,
            copiedRoiIds,
            skippedRois,
            emptyVolumeRoiIds: StructureSetCopyService.findEmptyVolumeRois(
                targetStructureSet,
                copiedRoiIds
            ),
            namingFailures,
            externalDemotions,
        };
    }

    public static buildSliceNormalPositionLookup(image: Image3D): {
        normalPosition: number;
        sopInstanceUid: string;
        sopClassUid: string;
    }[] {
        const normalDir = image.grid.getUnitZ();
        return image.imageSlices
            .map((slice) => ({
                normalPosition: vec3.dot(normalDir, slice.imagePosition),
                sopInstanceUid: slice.sopInstanceUid,
                sopClassUid: image.sopClassUid,
            }))
            .sort((a, b) => a.normalPosition - b.normalPosition);
    }

    /**
     * A copy of `sourceDicom` re-identified for `targetImage`, whose ROIs are
     * new structures with new ids, and each cloned ROI's source structure by
     * the clone's id.
     */
    private cloneAndReidentify(
        sourceDicom: DicomStructureSet,
        targetImage: Image3D,
        existingStructureSets: readonly StructureSet[],
        labelOverride?: string
    ): {
        clonedDicom: DicomStructureSet;
        sourceRoiOf: ReadonlyMap<RoiId, DicomRoi>;
    } {
        const clonedWrapper = new DcmJsDatasetWrapper(
            DicomProcessor.cloneDicomDataset(sourceDicom)
        );

        const newSopUid = generateUID();
        const newSeriesUid = generateUID();

        const targetSliceUids = targetImage.imageSlices.map(
            (slice) => slice.sopInstanceUid
        );

        DicomProcessor.setSopInstanceUid(clonedWrapper, newSopUid);
        DicomProcessor.setSeriesInstanceUid(clonedWrapper, newSeriesUid);
        DicomProcessor.setFrameOfReferenceUid(
            clonedWrapper,
            targetImage.frameOfReferenceUid
        );
        DicomProcessor.setReferencedSeriesUid(
            clonedWrapper,
            targetImage.seriesUid
        );
        DicomProcessor.setApprovalStatus(
            clonedWrapper,
            DICOMApprovalStatus.UNAPPROVED
        );

        this.updateReferencedImageSequence(
            clonedWrapper,
            targetImage.seriesUid,
            targetImage.sopClassUid,
            targetSliceUids
        );

        const parsedRois = DicomProcessor.parseRoisFromDataset(clonedWrapper);

        const newLabel =
            labelOverride ??
            generateUniqueStructureSetLabel(
                [...existingStructureSets],
                sourceDicom.structureSetLabel
            );

        const clonedDicom = new DicomStructureSet(
            clonedWrapper,
            newSopUid,
            targetImage.frameOfReferenceUid,
            targetImage.seriesUid,
            parsedRois,
            targetSliceUids,
            targetImage.sopClassUid,
            newSeriesUid
        );

        clonedDicom.structureSetLabel = newLabel;

        // The cloned file holds each ROI under the number the source file does.
        const sourceByNumberInFile = new Map(
            Array.from(sourceDicom.rois.values(), (roi) => [
                roi.roiNumberInFile,
                roi,
            ])
        );
        const sourceRoiOf = new Map<RoiId, DicomRoi>();
        for (const roi of parsedRois.values()) {
            const source = sourceByNumberInFile.get(roi.roiNumberInFile);
            if (source) {
                sourceRoiOf.set(roi.uid, source);
            }
        }
        return { clonedDicom, sourceRoiOf };
    }

    private updateReferencedImageSequence(
        dataset: DcmJsDatasetWrapper,
        seriesUid: string,
        sopClassUid: string,
        sopInstanceUids: string[]
    ): void {
        DicomProcessor.updateReferencedImageUids(
            dataset,
            seriesUid,
            sopClassUid,
            sopInstanceUids
        );
    }

    private remapAllContourReferences(
        copied: StructureSet,
        sourceRoiOf: ReadonlyMap<RoiId, DicomRoi>,
        sourceImage: Image3D,
        targetImage: Image3D,
        outCopied: RoiId[],
        outSkipped: SkippedRoi[]
    ): void {
        for (const roi of copied) {
            const isPoint =
                roi.interpretedType !== undefined &&
                ROI_POINT_TYPES.includes(
                    roi.interpretedType as RoiInterpretedType
                );

            if (isPoint) {
                outCopied.push(roi.uid);
                continue;
            }

            try {
                this.remapRoiContourReferences(
                    roi,
                    sourceImage,
                    targetImage,
                    copied.dicomStructureSet
                );
                outCopied.push(roi.uid);
            } catch (err) {
                outSkipped.push({
                    roiId: sourceRoiOf.get(roi.uid)?.uid ?? roi.uid,
                    roiName: roi.name,
                    reason:
                        err instanceof Error
                            ? err.message
                            : 'Unknown error during contour remapping',
                });
            }
        }
    }

    private remapRoiContourReferences(
        roi: DicomRoi,
        sourceImage: Image3D,
        targetImage: Image3D,
        targetDicom: DicomStructureSet
    ): void {
        const remapped = roi.contours.map((contour) => {
            if (!contour.referencedSOPInstanceUID) {
                return contour;
            }

            const sourceSliceZ = this.getSlicePositionForUid(
                sourceImage,
                contour.referencedSOPInstanceUID
            );
            if (sourceSliceZ === undefined) {
                throw new Error(
                    `Source slice not found for SOP UID ${contour.referencedSOPInstanceUID}`
                );
            }

            const targetUid = targetImage.getUidForSlice(sourceSliceZ);
            if (!targetUid) {
                throw new Error(`No target slice at z = ${sourceSliceZ} mm`);
            }

            return contour.withReferencedUids(
                targetDicom.referencedSopClassUid,
                targetUid
            );
        });

        roi.contours = remapped;
        targetDicom.updateContourSequence(roi.uid);
    }

    private getSlicePositionForUid(
        image: Image3D,
        sopInstanceUid: string
    ): number | undefined {
        const slice = image.imageSlices.find(
            (s) => s.sopInstanceUid === sopInstanceUid
        );
        if (!slice) {
            return undefined;
        }
        const normalDir = image.grid.getUnitZ();
        return vec3.dot(normalDir, slice.imagePosition);
    }

    /**
     * Transactionally replaces an existing structure with a new one.
     * Creates the new structure first, then removes the old one only if creation succeeds.
     * This prevents data loss if the replacement process fails. The
     * replacement is a new structure with an id of its own: it inherits
     * nothing stored for the old one, whose undo history and original mask are
     * forgotten with it.
     *
     * @param targetStructureSet The structure set to modify
     * @param existingRoiId Id of the structure to replace
     * @param sourceRoi Source ROI to copy properties from
     * @param contours Contours for the new structure
     * @returns Result indicating success/failure and details
     */
    private replaceStructureTransactionally(
        targetStructureSet: StructureSet,
        existingRoiId: RoiId,
        sourceRoi: DicomRoi,
        contours: DicomContour[]
    ): StructureReplacementResult {
        try {
            this.validateReplacementInputs(sourceRoi, contours);
        } catch (err) {
            return {
                success: false,
                error: {
                    phase: 'validation',
                    message: 'Pre-validation failed',
                    originalError:
                        err instanceof Error
                            ? err.message
                            : 'Unknown validation error',
                },
            };
        }

        const originalRoi =
            targetStructureSet.dicomStructureSet.getRoi(existingRoiId);
        if (!originalRoi) {
            return {
                success: false,
                error: {
                    phase: 'validation',
                    message: 'Original structure not found',
                    originalError: 'The structure to replace no longer exists',
                },
            };
        }

        // This method deletes the original ROI outright, so it refuses here
        // rather than trusting every caller to have checked.
        if (!originalRoi.canModifyContours) {
            return {
                success: false,
                error: {
                    phase: 'validation',
                    message:
                        originalRoi.contourEditBlocker === 'lock'
                            ? 'Original structure is locked'
                            : 'Original structure is approved',
                    originalError: `The structure to replace is ${originalRoi.locked ? 'locked' : 'approved'}`,
                },
            };
        }

        const tempName = this.nextTemporaryName(
            targetStructureSet,
            sourceRoi.name
        );
        let tempRoi: DicomRoi;
        // The replacement is created while the structure it replaces is still
        // in the set, so an EXTERNAL source overwriting the target's own
        // EXTERNAL is demoted here as well (creating a second EXTERNAL would
        // otherwise be rejected outright and lose the whole overwrite). The
        // demotion rides back to the caller so it can be reported.
        let demotedFromExternal = false;

        try {
            const copyable = copyableInterpretedType(
                targetStructureSet.dicomStructureSet,
                sourceRoi.interpretedType as RoiInterpretedType
            );
            demotedFromExternal = copyable.demotedFromExternal;
            tempRoi = targetStructureSet.dicomStructureSet.createRoi(
                copyable.interpretedType,
                tempName
            );

            tempRoi.color = {
                R: sourceRoi.color.R,
                G: sourceRoi.color.G,
                B: sourceRoi.color.B,
            };

            tempRoi.contours = contours;
            tempRoi.approvalStatus = DICOMApprovalStatus.UNAPPROVED;

            targetStructureSet.dicomStructureSet.updateContourSequence(
                tempRoi.uid
            );
        } catch (err) {
            return {
                success: false,
                error: {
                    phase: 'creation',
                    message: 'Failed to create replacement structure',
                    originalError:
                        err instanceof Error
                            ? err.message
                            : 'Unknown creation error',
                },
            };
        }

        try {
            targetStructureSet.dicomStructureSet.deleteRoi(existingRoiId);
        } catch (err) {
            try {
                targetStructureSet.dicomStructureSet.deleteRoi(tempRoi.uid);
            } catch (cleanupErr) {
                console.warn(
                    'Failed to cleanup temporary structure during rollback:',
                    cleanupErr
                );
            }

            return {
                success: false,
                error: {
                    phase: 'deletion',
                    message:
                        'Failed to remove original structure, replacement rolled back',
                    originalError:
                        err instanceof Error
                            ? err.message
                            : 'Unknown deletion error',
                },
            };
        }

        StructureSetService.forgetRoiSnapshots(existingRoiId);

        try {
            tempRoi.name = sourceRoi.name;
            targetStructureSet.dicomStructureSet.updateContourSequence(
                tempRoi.uid
            );

            return {
                success: true,
                roiId: tempRoi.uid,
                demotedFromExternal,
            };
        } catch (err) {
            // The data landed but the label did not. Report it so the caller
            // can warn the user instead of presenting a clean copy: the
            // clinician would otherwise see a `<name>_temp_<n>` structure
            // with no indication anything went wrong.
            console.error(
                'Failed to finalize structure name, structure exists with temporary name:',
                err
            );

            return {
                success: true,
                roiId: tempRoi.uid,
                temporaryName: tempName,
                demotedFromExternal,
            };
        }
    }

    /**
     * Builds a collision-free temporary name for the two-phase replacement.
     *
     * Uses a monotonic per-service counter rather than a clock/RNG so the
     * domain layer stays deterministic and testable, and verifies the name
     * is actually free in the target instead of relying on entropy.
     */
    private nextTemporaryName(
        targetStructureSet: StructureSet,
        baseName: string
    ): string {
        let candidate: string;
        do {
            this.temporaryNameCounter++;
            candidate = `${baseName}_temp_${this.temporaryNameCounter}`;
        } while (
            targetStructureSet.dicomStructureSet.hasRoiWithTrimmedName(
                candidate
            )
        );
        return candidate;
    }

    /**
     * Validates inputs for structure replacement to catch issues early.
     *
     * Note: `interpretedType` is validated with an explicit `undefined`
     * check rather than a falsy check because `RoiInterpretedType.UNKNOWN`
     * is defined as `''` (empty string) — a valid and common state in
     * real DICOM data that should not be rejected.
     */
    private validateReplacementInputs(
        sourceRoi: DicomRoi,
        contours: DicomContour[]
    ): void {
        if (!sourceRoi) {
            throw new Error('Source ROI is required');
        }

        if (!sourceRoi.name || sourceRoi.name.trim().length === 0) {
            throw new Error('Source ROI must have a valid name');
        }

        if (
            sourceRoi.interpretedType === undefined ||
            sourceRoi.interpretedType === null
        ) {
            throw new Error('Source ROI must have an interpreted type');
        }

        if (!Array.isArray(contours)) {
            throw new Error('Contours must be an array');
        }
    }
}
